import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DisputeResolution = ({ billingRecords, onRefresh }) => {
  const [selectedDispute, setSelectedDispute] = useState(null);

  // Identify potential disputes (failed or refunded payments)
  const disputes = billingRecords?.filter(
    record => record?.payment_status === 'failed' || record?.payment_status === 'refunded'
  ) || [];

  const getStatusBadgeColor = (status) => {
    const colors = {
      failed: 'bg-destructive/10 text-destructive',
      refunded: 'bg-warning/10 text-warning',
      pending: 'bg-primary/10 text-primary'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
          <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
          Dispute Resolution
        </h2>
        <span className="text-sm font-medium text-muted-foreground">
          {disputes?.length} dispute{disputes?.length !== 1 ? 's' : ''}
        </span>
      </div>
      {disputes?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Active Disputes</h3>
          <p className="text-muted-foreground">All payments are processing smoothly</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {disputes?.map((dispute) => (
            <div
              key={dispute?.id}
              className="p-4 bg-background rounded-lg border border-border hover:border-primary transition-smooth"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-foreground">
                      {dispute?.invoice_number}
                    </p>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusBadgeColor(dispute?.payment_status)}`}>
                      {dispute?.payment_status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Billing Date: {formatDate(dispute?.billing_date)}
                  </p>
                </div>
                <p className="text-lg font-bold text-destructive">
                  ${parseFloat(dispute?.total_amount || 0)?.toFixed(2)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Payment Method</p>
                  <p className="font-medium text-foreground capitalize">
                    {dispute?.payment_method?.replace('_', ' ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Transaction ID</p>
                  <p className="font-medium text-foreground text-xs">
                    {dispute?.transaction_id || 'N/A'}
                  </p>
                </div>
              </div>

              {dispute?.payment_notes && (
                <div className="mb-3 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
                  <Icon name="FileText" size={12} className="inline mr-1" />
                  {dispute?.payment_notes}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDispute(dispute)}
                  className="flex-1"
                >
                  <Icon name="Search" size={14} className="mr-1" />
                  Investigate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Icon name="MessageCircle" size={14} className="mr-1" />
                  Contact Member
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Investigation Modal */}
      {selectedDispute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card">
              <h2 className="text-xl font-bold text-foreground">Dispute Investigation</h2>
              <button
                onClick={() => setSelectedDispute(null)}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Invoice Number</p>
                  <p className="font-semibold text-foreground">{selectedDispute?.invoice_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  <span className={`inline-block text-xs px-3 py-1 rounded-full font-medium ${getStatusBadgeColor(selectedDispute?.payment_status)}`}>
                    {selectedDispute?.payment_status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Amount</p>
                  <p className="font-semibold text-foreground">${parseFloat(selectedDispute?.total_amount || 0)?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Billing Date</p>
                  <p className="font-semibold text-foreground">{formatDate(selectedDispute?.billing_date)}</p>
                </div>
              </div>

              <div className="p-4 bg-warning/5 rounded-lg border border-warning/20">
                <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
                  Resolution Actions
                </h3>
                <div className="space-y-2">
                  <Button variant="outline" fullWidth>
                    <Icon name="RefreshCw" size={16} className="mr-2" />
                    Retry Payment
                  </Button>
                  <Button variant="outline" fullWidth>
                    <Icon name="RotateCcw" size={16} className="mr-2" />
                    Issue Refund
                  </Button>
                  <Button variant="outline" fullWidth>
                    <Icon name="MessageCircle" size={16} className="mr-2" />
                    Send Resolution Email
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button variant="ghost" onClick={() => setSelectedDispute(null)}>
                  Close
                </Button>
                <Button>
                  Mark as Resolved
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeResolution;